import React, { useMemo } from "react";
import { useEvent } from "../../../../context/EventContext";
import eventsAPI from "../../../../api/eventsAPI";
import { useAuth } from "../../../../context/AuthContext";
import { toast } from "react-toastify";

// * RegistrationTable component renders tables for registration types and registered users.
//  *
//  * Props:
//  * - filterRegistrationType: A string for filtering by registration type.
//  * - setFilterRegistrationType: A function to set the filter for registration type.
//  * - sortJobStatus: A string for sorting by job status.
//  * - setSortJobStatus: A function to set the sort order by job status.
//  * - clearFiltersAndSorting: A function to clear all filters and sorting.
//  * - filteredData: An array containing the filtered registration data.
//  * - handleImageClick: A function to handle the click event on a user's profile picture.
//  * - registrationDetailsArray: An array with details of registration types.
//  * - registrationData: An array containing all registration data.
//  * - registeredType: The registration type the current user is registered for.
//  *
//  * This component displays a table of registration types with options to register
//  * and another table of registered users with filtering and sorting options.
//  */
const RegistrationTable = ({
  filterRegistrationType,
  setFilterRegistrationType,
  sortJobStatus,
  setSortJobStatus,
  clearFiltersAndSorting,
  filteredData,
  handleImageClick,
  registrationDetailsArray,
  registrationData,
  registeredType,
}) => {
  const { setLoading, selectedEvent } = useEvent();
  const { user } = useAuth();

  // Use useMemo to memoize the main event details and avoid unnecessary recalculations
  const mainEventEventDetails = useMemo(
    () => (selectedEvent && selectedEvent[0] ? selectedEvent[0] : null),
    [selectedEvent]
  );

  //   * Checks if the registration period has started.
  //   * The function takes the current date and the registration start date from
  //   * the event data and checks if the current date is after the start date.
  const isRegistrationClosed = () => {
    const currentDate = new Date();
    const startDate = new Date(mainEventEventDetails?.registration_start_date);

    // Reset time to 00:00:00 to compare only the date
    currentDate.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);

    // Check if the current date is after the registration start date
    return currentDate >= startDate;
  };

  // Sends a POST request to the server to add the registration data.
  const handleRegister = async (registrationTypeID) => {
    if (registeredType) {
      toast.error("You have already registered.");
      return;
    }
    setLoading(true);
    try {
      const response = await eventsAPI.post("/organizer/registrationdata", {
        userID: user?.id,
        registrationTypeID,
      });

      if (response.status === 200) {
        toast.success("Registration successfully!");
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.success("Registration Failed");
      console.log(error);
    }
  };

  return (
    <div className="registration-table">
      {registrationDetailsArray.length > 0 ? (
        <>
          <div className="user-register-table">
            <table>
              <thead>
                <tr>
                  <th>Type of Registration</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Number of Registration</th>
                  <th>Registration</th>
                </tr>
              </thead>
              <tbody>
                {registrationDetailsArray.map((registration, index) => (
                  <tr key={index}>
                    <td>{registration.typeName}</td>
                    <td>{registration.description}</td>
                    <td>{registration.price}</td>
                    <td>
                      {
                        registrationData.filter(
                          (data) =>
                            data.registrationTypeID._id === registration._id
                        ).length
                      }
                    </td>
                    <td>
                      {
                      isRegistrationClosed() ? (
                        <p>Registration Closed</p>
                      ) : 
                      registeredType === registration._id ? (
                        <p>Already Registered</p>
                      ) : (
                        <button
                          className="register-btn"
                          onClick={() => handleRegister(registration._id)}
                          disabled={!!registeredType} // Disable button if the user is registered for another type
                        >
                          Register
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p>No Registration Types Details found.</p>
      )}

      {/* Registered Users Table (New Table) */}
      {registrationData.length > 0 && (
        <>
          <div className="sorting-filter">
            <p>Filter By Registration Type</p>
            <select
              value={filterRegistrationType}
              onChange={(e) => setFilterRegistrationType(e.target.value)}
            >
              <option value="">select</option>
              {registrationDetailsArray.map((registration) => (
                <option key={registration._id} value={registration._id}>
                  {registration.typeName}
                </option>
              ))}
            </select>
            <select
              value={sortJobStatus}
              onChange={(e) => setSortJobStatus(e.target.value)}
            >
              <option>Sort By Job Status</option>
              <option value="student">Student</option>
              <option value="employed">Employed</option>
              <option value="unemployed">Unemployed</option>
              <option value="self-employed">Self-Employed</option>
            </select>
            <button
              className="add-space"
              onClick={() => clearFiltersAndSorting()}
            >
              Clear
            </button>
          </div>
          <div className="user-registration-table">
            <h3>Registered Users</h3>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Profile Picture</th>
                  <th>Registration Type</th>
                  <th>Job Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((registration, index) => (
                    <tr key={index}>
                      <td>{registration.userID?.firstName}</td>
                      <td>{registration.userID?.email}</td>
                      <td>
                        <img
                          src={registration.userID?.profilePicture}
                          alt="Profile Pic"
                          onClick={() => handleImageClick(registration.userID)} // Handle image click to show the "Hi" message
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            cursor: "pointer",
                          }}
                        />
                      </td>
                      <td>{registration.registrationTypeID?.typeName}</td>
                      <td>{registration.userID?.jobStatus}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">
                      <p>No data available</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default RegistrationTable;
